import { Tr, Td, Badge, Text, Tooltip, Box, HStack, VStack } from "@chakra-ui/react";

// ─── Mobile Card ─────────────────────────────────────────────
const DividendCard = ({ dividend, netAmount, taxPercent }) => (
  <Box
    p={3}
    bg="rgba(72, 187, 120, 0.05)"
    borderRadius="xl"
    border="1px solid rgba(72, 187, 120, 0.15)"
    transition="all 0.25s"
    _hover={{
      bg: "rgba(72, 187, 120, 0.1)",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
    }}
  >
    {/* Header: Ticker + Date */}
    <HStack justify="space-between" mb={2}>
      <HStack spacing={2}>
        <Badge colorScheme="green" fontSize="2xs" px={1.5} borderRadius="md">
          {dividend.ticker}
        </Badge>
        {taxPercent > 0 && (
          <Badge colorScheme="red" fontSize="2xs" px={1.5} borderRadius="md" variant="subtle">
            -{taxPercent.toFixed(0)}% tax
          </Badge>
        )}
      </HStack>
      <Text fontSize="xs" color="gray.400">
        {new Date(dividend.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "2-digit",
        })}
      </Text>
    </HStack>

    {/* Amounts row */}
    <HStack justify="space-between" align="baseline">
      <VStack spacing={0} align="start">
        <Text fontSize="2xs" color="gray.500">Gross</Text>
        <Text fontSize="sm" fontWeight="bold" color="green.300">
          ${Number(dividend.amount).toFixed(2)}
        </Text>
      </VStack>
      <VStack spacing={0} align="center">
        <Text fontSize="2xs" color="gray.500">Tax</Text>
        <Text fontSize="sm" fontWeight="medium" color="red.300">
          -${Number(dividend.tax).toFixed(2)}
        </Text>
      </VStack>
      <VStack spacing={0} align="end">
        <Text fontSize="2xs" color="gray.500">Net</Text>
        <Text fontSize="md" fontWeight="black" color="teal.300">
          ${netAmount.toFixed(2)}
        </Text>
      </VStack>
    </HStack>

    {/* Description — only if exists */}
    {dividend.description && (
      <Text fontSize="2xs" color="gray.500" mt={1.5} noOfLines={1}>
        {dividend.description}
      </Text>
    )}
  </Box>
);

// ─── Desktop Table Row ───────────────────────────────────────
const DividendTableRow = ({ dividend, netAmount }) => (
  <Tr
    _hover={{
      bg: "green.900",
      transform: "scale(1.001)",
      boxShadow: "0 2px 8px rgba(0,255,0,0.1)",
    }}
    transition="all 0.2s ease"
    borderBottom="1px solid rgba(72, 187, 120, 0.2)"
    bg="rgba(72, 187, 120, 0.05)"
  >
    <Td textAlign="center" fontSize="sm" color="gray.300">
      {new Date(dividend.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })}
    </Td>

    <Td textAlign="center">
      <Badge colorScheme="green" fontSize="xs" px={2} py={1} borderRadius="md">
        {dividend.ticker}
      </Badge>
    </Td>

    <Td textAlign="center" fontSize="xs" color="gray.400" maxW="400px" isTruncated>
      <Tooltip label={dividend.description} hasArrow placement="top">
        {dividend.description || "—"}
      </Tooltip>
    </Td>

    <Td textAlign="center" fontWeight="bold" color="green.300" fontSize="sm">
      ${Number(dividend.amount).toFixed(4)}
    </Td>

    <Td textAlign="center" fontWeight="medium" color="red.300" fontSize="sm">
      ${Number(dividend.tax).toFixed(4)}
    </Td>

    <Td textAlign="center" fontWeight="bold" color="teal.300" fontSize="sm">
      <Tooltip label="Amount after tax" hasArrow placement="top">
        ${netAmount.toFixed(4)}
      </Tooltip>
    </Td>
  </Tr>
);

// ─── Main Export ─────────────────────────────────────────────
const DividendRow = ({ dividend, isMobile = false }) => {
  const netAmount = Number(dividend.amount) - Number(dividend.tax);
  const taxPercent =
    Number(dividend.amount) > 0
      ? (Number(dividend.tax) / Number(dividend.amount)) * 100
      : 0;

  if (isMobile) {
    return (
      <DividendCard
        dividend={dividend}
        netAmount={netAmount}
        taxPercent={taxPercent}
      />
    );
  }

  return <DividendTableRow dividend={dividend} netAmount={netAmount} />;
};

export default DividendRow;